import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  verifyBeforeUpdateEmail,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  function logout() {
    return signOut(auth);
  }

  function reauthenticate(password) {
    if (!currentUser || !currentUser.email) {
      return Promise.reject(new Error('No user is currently logged in'));
    }
    
    const credential = EmailAuthProvider.credential(
      currentUser.email,
      password
    );
    return reauthenticateWithCredential(currentUser, credential);
  }

  function updateUserPassword(newPassword) {
    return updatePassword(currentUser, newPassword);
  }

  function updateUserEmail(newEmail) {
    // Use verifyBeforeUpdateEmail to send a verification email
    // The email will only be updated after the user clicks the link in the verification email
    return verifyBeforeUpdateEmail(currentUser, newEmail);
  }

  async function resetPassword(email) {
    // First check if the email exists
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      
      // If methods array is empty, no user exists with this email
      if (methods.length === 0) {
        throw new Error('user-not-found');
      }
      
      // If we get here, the email exists, so send the reset email
      return sendPasswordResetEmail(auth, email);
    } catch (error) {
      // If fetchSignInMethodsForEmail fails with auth/invalid-email, pass that through
      if (error.code === 'auth/invalid-email') {
        throw error;
      }
      
      // For our custom error
      if (error.message === 'user-not-found') {
        const customError = new Error('No account exists with this email address.');
        customError.code = 'auth/user-not-found';
        throw customError;
      }
      
      // For any other errors, pass them through
      throw error;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    signInWithGoogle,
    logout,
    reauthenticate,
    updateUserPassword,
    updateUserEmail,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
